import { computed, onScopeDispose, ref } from 'vue'
import { defineStore } from 'pinia'
import type {
  OfflineCommandStatus,
  OfflineOperatingMode,
  OfflinePosContext,
} from '@/offline/contracts'
import { db, type BigSaleDB } from '@/offline/db'
import { buildOfflineContextKey, getOrCreateDeviceId } from '@/offline/device-id'

const UNRESOLVED_COMMAND_STATUSES = new Set<OfflineCommandStatus>([
  'pending',
  'syncing',
  'failed',
  'blocked',
])

export const useOfflineModeStore = defineStore('offline-mode', () => {
  const mode = ref<OfflineOperatingMode>('online')
  const isConnected = ref(typeof navigator === 'undefined' ? true : navigator.onLine)
  const contextKey = ref<string | null>(null)
  const bootstrapId = ref<string | null>(null)
  const bootstrapAt = ref<string | null>(null)
  const pendingCount = ref(0)
  const syncCompletedCount = ref(0)
  const syncTotalCount = ref(0)
  const syncErrorCode = ref<string | null>(null)
  const activationErrorCode = ref<string | null>(null)
  const lastBootstrapErrorCode = ref<string | null>(null)
  const reconnectPromptDismissed = ref(false)

  const isOnlineMode = computed(() => mode.value === 'online')
  const isOfflineMode = computed(() => mode.value === 'offline')
  const isSyncing = computed(() => mode.value === 'syncing')
  const isOfflineReady = computed(() => bootstrapId.value !== null)
  const showReconnectPrompt = computed(
    () =>
      mode.value === 'offline' &&
      isConnected.value &&
      !reconnectPromptDismissed.value &&
      syncErrorCode.value === null,
  )

  let database: BigSaleDB = db
  let listenersRegistered = false

  function setConnectivity(connected: boolean) {
    if (!connected) reconnectPromptDismissed.value = false
    isConnected.value = connected
  }

  function continueOffline() {
    if (mode.value !== 'offline') return
    reconnectPromptDismissed.value = true
  }

  const handleOnline = () => setConnectivity(true)
  const handleOffline = () => setConnectivity(false)

  function registerConnectivityListeners() {
    if (listenersRegistered || typeof window === 'undefined') {
      return
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    listenersRegistered = true
  }

  function removeConnectivityListeners() {
    if (!listenersRegistered || typeof window === 'undefined') {
      return
    }

    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
    listenersRegistered = false
  }

  async function persistContext(
    changes: Partial<Pick<OfflinePosContext, 'mode' | 'bootstrap_id' | 'bootstrap_at'>>,
  ) {
    if (contextKey.value === null) {
      return
    }

    const updated = await database.offlineContexts.update(contextKey.value, changes)
    if (updated === 0) {
      throw new Error('OFFLINE_CONTEXT_MISSING')
    }
  }

  async function refreshPendingCount() {
    if (contextKey.value === null) {
      pendingCount.value = 0
      return 0
    }

    const activeContextKey = contextKey.value
    const unresolvedKeys: [string, OfflineCommandStatus][] = Array.from(
      UNRESOLVED_COMMAND_STATUSES,
      (status) => [activeContextKey, status],
    )
    const count = await database.outboxCommands
      .where('[context_key+status]')
      .anyOf(unresolvedKeys)
      .count()
    pendingCount.value = count

    return count
  }

  async function refreshBootstrapMetadata() {
    if (contextKey.value === null) {
      throw new Error('OFFLINE_CONTEXT_MISSING')
    }

    const storedContext = await database.offlineContexts.get(contextKey.value)
    if (storedContext === undefined) {
      throw new Error('OFFLINE_CONTEXT_MISSING')
    }

    bootstrapId.value = storedContext.bootstrap_id
    bootstrapAt.value = storedContext.bootstrap_at
  }

  async function initializeContext(context: OfflinePosContext, contextDatabase: BigSaleDB = db) {
    const deviceId = getOrCreateDeviceId()
    if (context.device_id !== deviceId) {
      throw new Error('OFFLINE_CONTEXT_DEVICE_MISMATCH')
    }

    const expectedContextKey = buildOfflineContextKey(
      context.tenant_code,
      context.branch_id,
      deviceId,
    )
    if (context.context_key !== expectedContextKey) {
      throw new Error('OFFLINE_CONTEXT_SCOPE_MISMATCH')
    }

    database = contextDatabase
    const storedContext = await database.offlineContexts.get(expectedContextKey)
    let activeContext = storedContext ?? context

    if (storedContext === undefined) {
      await database.offlineContexts.put(context)
    } else if (storedContext.mode === 'syncing') {
      activeContext = { ...storedContext, mode: 'offline' }
      await database.offlineContexts.update(expectedContextKey, {
        mode: 'offline',
      })
    }

    contextKey.value = activeContext.context_key
    mode.value = activeContext.mode
    bootstrapId.value = activeContext.bootstrap_id
    bootstrapAt.value = activeContext.bootstrap_at
    syncCompletedCount.value = 0
    syncTotalCount.value = 0
    syncErrorCode.value = null
    activationErrorCode.value = null
    lastBootstrapErrorCode.value = null
    reconnectPromptDismissed.value = false
    registerConnectivityListeners()
    setConnectivity(typeof navigator === 'undefined' ? true : navigator.onLine)
    await refreshPendingCount()
  }

  function blockContextActivation(code: string) {
    deactivateContext()
    activationErrorCode.value = code
  }

  function setLastBootstrapError(code: string | null) {
    lastBootstrapErrorCode.value = code
  }

  function requireContextKey(): string {
    if (activationErrorCode.value) throw new Error(activationErrorCode.value)
    if (!contextKey.value) throw new Error('OFFLINE_CONTEXT_MISSING')

    return contextKey.value
  }

  function deactivateContext() {
    removeConnectivityListeners()
    database = db
    mode.value = 'online'
    isConnected.value = typeof navigator === 'undefined' ? true : navigator.onLine
    contextKey.value = null
    bootstrapId.value = null
    bootstrapAt.value = null
    pendingCount.value = 0
    syncCompletedCount.value = 0
    syncTotalCount.value = 0
    syncErrorCode.value = null
    activationErrorCode.value = null
    lastBootstrapErrorCode.value = null
    reconnectPromptDismissed.value = false
  }

  function enterOffline() {
    if (!isOfflineReady.value) {
      throw new Error('OFFLINE_BOOTSTRAP_REQUIRED')
    }

    mode.value = 'offline'
    lastBootstrapErrorCode.value = null
    return persistContext({ mode: 'offline' })
  }

  function beginSync(total: number) {
    if (mode.value !== 'offline' || !isConnected.value) {
      throw new Error('OFFLINE_SYNC_NOT_READY')
    }

    mode.value = 'syncing'
    syncTotalCount.value = total
    syncCompletedCount.value = 0
    syncErrorCode.value = null
    reconnectPromptDismissed.value = true
  }

  function recordSyncProgress(completed: number) {
    if (mode.value !== 'syncing' || !Number.isFinite(completed)) return
    syncCompletedCount.value = Math.min(
      syncTotalCount.value,
      Math.max(syncCompletedCount.value, Math.floor(completed)),
    )
  }

  function syncFailed(code: string) {
    mode.value = 'offline'
    syncErrorCode.value = code
    reconnectPromptDismissed.value = true
    return persistContext({ mode: 'offline' })
  }

  async function syncCompleted(freshBootstrapId: string) {
    if (mode.value !== 'syncing' || pendingCount.value !== 0) {
      throw new Error('OFFLINE_SYNC_INCOMPLETE')
    }

    const previousBootstrapId = bootstrapId.value

    try {
      if (contextKey.value === null) {
        throw new Error('OFFLINE_CONTEXT_MISSING')
      }

      await persistContext({
        mode: 'online',
        bootstrap_id: freshBootstrapId,
      })
    } catch (error) {
      mode.value = 'offline'
      bootstrapId.value = previousBootstrapId
      syncErrorCode.value = 'OFFLINE_SYNC_PERSIST_FAILED'
      throw error
    }

    bootstrapId.value = freshBootstrapId
    mode.value = 'online'
    syncCompletedCount.value = syncTotalCount.value
    reconnectPromptDismissed.value = false
  }

  onScopeDispose(removeConnectivityListeners)

  return {
    mode,
    isConnected,
    contextKey,
    bootstrapId,
    bootstrapAt,
    pendingCount,
    syncCompletedCount,
    syncTotalCount,
    syncErrorCode,
    activationErrorCode,
    lastBootstrapErrorCode,
    isOnlineMode,
    isOfflineMode,
    isSyncing,
    isOfflineReady,
    showReconnectPrompt,
    initializeContext,
    blockContextActivation,
    setLastBootstrapError,
    requireContextKey,
    deactivateContext,
    enterOffline,
    beginSync,
    syncFailed,
    syncCompleted,
    setConnectivity,
    continueOffline,
    recordSyncProgress,
    refreshBootstrapMetadata,
    refreshPendingCount,
  }
})
